import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useChannels } from '@mint-lib/hooks';
import styled, { css } from '@mint-lib/styled';

import {
  BUILDER_ROW_TYPES,
  ROW_HEIGHT,
} from '../../../../../../constants/campaignBuilder.js';
import { Columns } from '../../../../../../constants/myCampaigns.js';
import { useTableScale } from '../../../../../../hooks/useTableScale.js';
import {
  VersionsEventEmitterAction,
  VersionsTableRow,
  VersionTableColumn,
} from '../../../../../../types/versions.js';
import eventEmitter from '../../../../../../utils/eventEmitter.js';

type Props = {
  cell: {
    column: VersionTableColumn;
    render: (type: string, { cellColor }: { cellColor: string }) => ReactNode;
  };
  row: VersionsTableRow;
};

const VersionsTableBodyItem = memo(({ cell, row }: Props) => {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const scale = useTableScale();

  const { name, type } = row.original;
  const isExpander = cell.column.id === Columns.EXPANDER;
  const isSticky = cell.column.id === Columns.ACTIONS_MENU;
  const isVersionType = type === BUILDER_ROW_TYPES.VERSION;
  const channelColors = useChannels();

  const backgroundColor = channelColors[name]?.backgroundColor || 'transparent';

  const handleOpenActionsMenu = useCallback(
    (rowId: string) => {
      if (rowId !== String(row.id)) {
        return;
      }
      setIsActionsMenuOpen(true);
    },
    [row, isActionsMenuOpen],
  );

  const handleCloseActionsMenu = useCallback(
    (rowId: string) => {
      if (rowId !== String(row.id)) {
        return;
      }
      setIsActionsMenuOpen(false);
    },
    [row],
  );

  useEffect(() => {
    if (!isVersionType || !isSticky) {
      return;
    }
    eventEmitter.addListener(
      VersionsEventEmitterAction.OPEN_MODAL,
      handleOpenActionsMenu,
    );

    eventEmitter.addListener(
      VersionsEventEmitterAction.CLOSE_MODAL,
      handleCloseActionsMenu,
    );

    return () => {
      eventEmitter.removeListener(
        VersionsEventEmitterAction.OPEN_MODAL,
        handleOpenActionsMenu,
      );
      eventEmitter.removeListener(
        VersionsEventEmitterAction.CLOSE_MODAL,
        handleCloseActionsMenu,
      );
    };
  }, [isVersionType, isExpander, cell]);

  const zIndex = useMemo(() => {
    return isActionsMenuOpen && isVersionType ? 2 : 1;
  }, [isActionsMenuOpen, isVersionType]);

  return (
    <Td
      isSticky={isSticky}
      isExpander={isExpander}
      isCurrentVersionField={!!row.original?.isCurrentVersion}
      $width={Number(cell.column.width)}
      $depth={row.depth}
      $height={ROW_HEIGHT}
      className="td"
      $scale={scale}
      $zIndex={zIndex}
      isActionsMenuOpen={isActionsMenuOpen}
    >
      {cell.render('Cell', { cellColor: backgroundColor })}
    </Td>
  );
});

const Td = styled('div')<{
  isSticky: boolean;
  isCurrentVersionField: boolean;
  isActionsMenuOpen: boolean;
  isExpander: boolean;
  $width: number;
  $height: number;
  $depth: number;
  $scale: number;
  $zIndex: number;
}>`
  position: relative;
  border-right: 1px solid ${({ theme }) => theme.palette.border.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.secondary};
  border-top: unset;
  text-align: right;
  vertical-align: middle;
  background-color: ${({ isActionsMenuOpen, theme }) =>
    isActionsMenuOpen
      ? theme.palette.background.primary
      : theme.palette.ui.primary};

  &:nth-last-child(2) {
    border-right: unset;
  }
  &:last-child {
    border-left: 1px solid ${({ theme }) => theme.palette.border.secondary};
  }

  ${({ isCurrentVersionField, theme }) =>
    isCurrentVersionField &&
    css`
      color: ${theme.palette.dataVisualization.emerald600};
    `}

  ${({ isExpander }) =>
    isExpander &&
    css`
      position: sticky;
      left: 0;
      z-index: 1;
    `}

  height: ${({ $height, $scale }) => $height * $scale}px;

  ${({ isSticky, $zIndex }) =>
    isSticky &&
    css`
      position: sticky;
      right: 0;
      z-index: ${$zIndex};
      box-shadow: -4px 0 6px rgba(0, 22, 14, 0.05);
      transition: 200ms;
    `}
  ${({ $width, $scale }) => {
    return css`
      width: ${$width * $scale}px;
      min-width: ${$width * $scale}px;
      max-width: ${$width * $scale}px;
    `;
  }}
`;

export default VersionsTableBodyItem;
